import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiRefreshCw } from "react-icons/fi";
import { useAuthFetch } from "../hooks/useAuthFetch";
import {
  CURATED_FIELDS,
  FIELD_GROUPS,
  SOURCES,
  SOURCE_IDS,
  describeField,
  discoverRawFields,
  resolveFieldValue,
} from "../utils/reportFieldCatalog";
import { loadProjectFieldContext, readSelectedProjectDetails } from "../utils/reportFieldData";
import searchIcon from "../img/7481d4f175f497fa3ca635d9ea8974d559a00bf54762cdfe73ec49ac971c04f3.svg";
import "./ReportModals.css";
import "./contacts/AddContactModal.css";

const GROUP_LABEL = Object.fromEntries(FIELD_GROUPS.map((g) => [g.id, g.label]));
const ALL_API = "__api__";
const SELECTED = "__selected__";

const curated = CURATED_FIELDS.map((f) => ({
  key: f.key,
  label: f.label,
  group: f.group,
  groupLabel: GROUP_LABEL[f.group] || f.group,
  sub: f.sub || "",
}));

function matches(field, q, sample) {
  if (!q) return true;
  const hay = `${field.label} ${field.key} ${field.sub || ""} ${field.groupLabel || ""} ${sample || ""}`.toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((part) => hay.includes(part));
}

function groupBySub(fields, fallbackTitle) {
  const map = new Map();
  fields.forEach((f) => {
    const title = f.sub || fallbackTitle;
    if (!map.has(title)) map.set(title, []);
    map.get(title).push(f);
  });
  return [...map.entries()].map(([title, list]) => ({ title, fields: list }));
}

/**
 * Modal that lists every API field (grouped) and lets the admin choose which ones
 * the AI agent must place in the template.
 */
export default function ReportFieldPicker({ open, onClose, selected, onChange, onSamples }) {
  const { authFetch, getAuthConfig, isReady } = useAuthFetch();
  const [activeGroup, setActiveGroup] = useState("project");
  const [query, setQuery] = useState("");
  const [rawFields, setRawFields] = useState([]);
  const [samples, setSamples] = useState({});
  const [sampleName, setSampleName] = useState("");
  const [sampleErrors, setSampleErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [opps, setOpps] = useState(null);
  const [oppQuery, setOppQuery] = useState("");
  const [oppOpen, setOppOpen] = useState(false);
  const autoLoaded = useRef(false);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const setKeys = useCallback(
    (keys, on) => {
      const next = new Set(selected);
      keys.forEach((k) => (on ? next.add(k) : next.delete(k)));
      onChange([...next]);
    },
    [selected, onChange]
  );

  const loadSample = useCallback(
    async (opportunityId, known = {}) => {
      if (!opportunityId || !isReady) return;
      setLoading(true);
      try {
        const ctx = await loadProjectFieldContext({
          opportunityId,
          selected: known,
          getAuthConfig,
          sources: "all",
        });
        const discovered = SOURCE_IDS.flatMap((id) => (ctx.raw[id] ? discoverRawFields(id, ctx.raw[id]) : []));
        const nextSamples = {};
        CURATED_FIELDS.forEach((f) => {
          const v = resolveFieldValue(f.key, ctx);
          if (v) nextSamples[f.key] = v;
        });
        discovered.forEach((f) => {
          if (f.sample) nextSamples[f.key] = f.sample;
        });
        setRawFields(discovered);
        setSamples(nextSamples);
        setSampleErrors(ctx.errors);
        setSampleName(
          ctx.raw.opportunity?.Project__r?.Name || ctx.raw.opportunity?.Name || known.projectName || opportunityId
        );
        onSamples?.(nextSamples);
      } catch (error) {
        setSampleErrors({ project: error?.message || "Could not load the project" });
      } finally {
        setLoading(false);
      }
    },
    [getAuthConfig, isReady, onSamples]
  );

  // First time the picker opens, use the project that is currently selected in the dashboard.
  useEffect(() => {
    if (!open || autoLoaded.current || !isReady) return;
    const current = readSelectedProjectDetails();
    if (current?.id_project) {
      autoLoaded.current = true;
      loadSample(current.id_project, current);
    }
  }, [open, isReady, loadSample]);

  const ensureOpps = useCallback(async () => {
    if (opps || !isReady) return;
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/all-opportunities`);
      const body = await response.json();
      setOpps(
        (body.records || []).map((r) => ({
          id: r.Id,
          name: r.Project__r?.Name || r.Name || r.Id,
          no: r.Project__r?.Project_System_Wide_ID__c || "",
          customer: r.Contract_With__r?.Name || "",
        }))
      );
    } catch {
      setOpps([]);
    }
  }, [authFetch, isReady, opps]);

  const oppMatches = useMemo(() => {
    if (!opps) return [];
    const q = oppQuery.trim().toLowerCase();
    const list = q
      ? opps.filter((o) => `${o.name} ${o.no} ${o.customer}`.toLowerCase().includes(q))
      : opps;
    return list.slice(0, 8);
  }, [opps, oppQuery]);

  const allFields = useMemo(
    () => [
      ...curated,
      ...rawFields.map((f) => ({ ...f, groupLabel: "All API fields" })),
    ],
    [rawFields]
  );

  const sections = useMemo(() => {
    const q = query.trim();
    if (q) {
      const list = allFields.filter((f) => matches(f, q, samples[f.key]));
      const byGroup = new Map();
      list.forEach((f) => {
        const title = f.group === "api" ? `All API fields / ${f.sub}` : f.sub ? `${f.groupLabel} / ${f.sub}` : f.groupLabel;
        if (!byGroup.has(title)) byGroup.set(title, []);
        byGroup.get(title).push(f);
      });
      return [...byGroup.entries()].map(([title, fields]) => ({ title, fields }));
    }
    if (activeGroup === SELECTED) {
      const known = new Map(allFields.map((f) => [f.key, f]));
      const fields = selected.map((key) => known.get(key) || { key, ...describeField(key) });
      return groupBySub(fields, "Selected fields").map((s) => ({
        ...s,
        title: s.title,
      }));
    }
    if (activeGroup === ALL_API) {
      return groupBySub(rawFields.map((f) => ({ ...f, groupLabel: "All API fields" })), "API");
    }
    const fields = curated.filter((f) => f.group === activeGroup);
    const subs = groupBySub(fields, GROUP_LABEL[activeGroup]);
    return subs;
  }, [activeGroup, allFields, query, rawFields, samples, selected]);

  const visibleKeys = useMemo(() => sections.flatMap((s) => s.fields.map((f) => f.key)), [sections]);

  const countFor = (group) => {
    if (group === ALL_API) return rawFields.filter((f) => selectedSet.has(f.key)).length;
    return curated.filter((f) => f.group === group && selectedSet.has(f.key)).length;
  };

  if (!open) return null;

  const navButton = (id, label, count, total) => (
    <button
      key={id}
      type="button"
      onClick={() => {
        setActiveGroup(id);
        setQuery("");
      }}
      className={`rpt-nav-item ${activeGroup === id && !query ? "is-active" : ""}`}
    >
      <span className="rpt-nav-label">{label}</span>
      {(count > 0 || total !== "") && (
        <span className={`rpt-badge ${count ? "is-on" : ""}`}>{count ? count : total}</span>
      )}
    </button>
  );

  const renderRow = (f, first) => {
    const on = selectedSet.has(f.key);
    const sample = samples[f.key];
    return (
      <label key={f.key} className={`rpt-row ${first ? "is-first" : ""}`}>
        <input type="checkbox" checked={on} onChange={() => setKeys([f.key], !on)} />
        <span className="rpt-row-main">
          <span className="rpt-row-label">{f.label}</span>
          <span className="rpt-row-key">{f.key}</span>
        </span>
        {sample ? (
          <span className="rpt-row-sample" title={sample}>
            {sample}
          </span>
        ) : sampleName ? (
          <span className="rpt-row-sample is-empty">empty</span>
        ) : null}
      </label>
    );
  };

  const errorEntries = Object.entries(sampleErrors || {});
  const title = query
    ? `Search results (${visibleKeys.length})`
    : activeGroup === SELECTED
    ? "Selected fields"
    : activeGroup === ALL_API
    ? "All API fields from the sample project"
    : GROUP_LABEL[activeGroup];

  return createPortal(
    <div
      className="add-contact-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="API fields"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="add-contact-modal-sheet rpt-sheet-wide">
        <div className="add-contact-modal-header">
          <div style={{ minWidth: 0 }}>
            <h2 className="add-contact-modal-title">API fields</h2>
            <p className="add-contact-modal-subtitle">
              Select the fields the template needs. Each one is placed in the template and filled with the project&apos;s
              value in Reports.
            </p>
          </div>
          <button type="button" className="add-contact-modal-close" onClick={onClose} aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z" fill="#424143" />
            </svg>
          </button>
        </div>

        <div className="rpt-toolbar">
          <div className="rpt-toolbar-field">
            <label className="block text-sm text-gray-600 mb-1" htmlFor="rpt-field-search">Search fields</label>
            <input
              id="rpt-field-search"
              className="rpt-input-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, key or value"
            />
            <img src={searchIcon} alt="" className="rpt-search-icon" />
          </div>
          <div className="rpt-toolbar-field">
            <label className="block text-sm text-gray-600 mb-1" htmlFor="rpt-sample-project">Sample project</label>
            <input
              id="rpt-sample-project"
              value={oppOpen ? oppQuery : sampleName}
              onFocus={() => {
                setOppOpen(true);
                setOppQuery("");
                ensureOpps();
              }}
              onBlur={() => setTimeout(() => setOppOpen(false), 150)}
              onChange={(e) => setOppQuery(e.target.value)}
              placeholder="Pick a project to list every API field"
              autoComplete="off"
            />
            {oppOpen && (
              <div className="rpt-suggest">
                {!opps && <div className="rpt-suggest-note">Loading projects...</div>}
                {opps && !oppMatches.length && <div className="rpt-suggest-note">No projects found</div>}
                {oppMatches.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    className="rpt-suggest-item"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setOppOpen(false);
                      loadSample(o.id);
                    }}
                  >
                    {o.name}
                    <small> {[o.no, o.customer].filter(Boolean).join(" · ")}</small>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            className="rpt-btn--sm rpt-btn--field"
            disabled={loading || !sampleName}
            onClick={() => {
              const cur = readSelectedProjectDetails();
              if (cur?.id_project) loadSample(cur.id_project, cur);
            }}
            title="Reload sample values from the selected project"
          >
            <FiRefreshCw className={loading ? "animate-spin" : ""} /> Reload
          </button>
        </div>
        {(loading || errorEntries.length > 0) && (
          <div className="rpt-status">
            {loading
              ? "Loading API data for the sample project..."
              : `Some sources returned no data: ${errorEntries
                  .map(([id, msg]) => `${SOURCES[id]?.label || id} (${msg})`)
                  .join(", ")}`}
          </div>
        )}

        <div className="rpt-split">
          <nav className="rpt-nav">
            {navButton(SELECTED, "Selected fields", selected.length, "")}
            <div className="rpt-nav-divider" />
            {FIELD_GROUPS.map((g) =>
              navButton(g.id, g.label, countFor(g.id), curated.filter((f) => f.group === g.id).length)
            )}
            <div className="rpt-nav-divider" />
            {navButton(ALL_API, "All API fields (live)", countFor(ALL_API), rawFields.length || "")}
          </nav>

          <div className="rpt-content">
            <div className="rpt-mobile-select">
              <label className="block text-sm text-gray-600 mb-1" htmlFor="rpt-group-select">Field group</label>
              <select
                id="rpt-group-select"
                value={activeGroup}
                onChange={(e) => {
                  setActiveGroup(e.target.value);
                  setQuery("");
                }}
              >
                <option value={SELECTED}>Selected fields ({selected.length})</option>
                {FIELD_GROUPS.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label} ({countFor(g.id)})
                  </option>
                ))}
                <option value={ALL_API}>All API fields (live)</option>
              </select>
            </div>

            <div className="rpt-content-head">
              <h3 className="rpt-content-title">{title}</h3>
              {visibleKeys.length > 0 && (
                <div className="rpt-actions">
                  <button type="button" className="rpt-btn--sm" onClick={() => setKeys(visibleKeys, true)}>
                    Select all shown
                  </button>
                  <button type="button" className="rpt-btn--sm" onClick={() => setKeys(visibleKeys, false)}>
                    Clear shown
                  </button>
                </div>
              )}
            </div>

            {activeGroup === ALL_API && !query && !rawFields.length && (
              <div className="rpt-empty">
                {loading
                  ? "Reading the API responses..."
                  : "Pick a sample project above. Every field its APIs return (project, info, contacts, financial, scheduling, production) is listed here, grouped by API."}
              </div>
            )}
            {activeGroup === SELECTED && !query && !selected.length && (
              <div className="rpt-empty">No fields selected yet. Pick fields from the groups on the left.</div>
            )}

            {sections.map((section) => {
              const keys = section.fields.map((f) => f.key);
              const chosen = keys.filter((k) => selectedSet.has(k)).length;
              const flat = sections.length === 1 && !query;
              const startOpen = Boolean(query) || flat;
              if (flat) {
                return (
                  <div key={section.title} className="rpt-section">
                    {section.fields.map((f, i) => renderRow(f, i === 0))}
                  </div>
                );
              }
              return (
                <details key={section.title} open={startOpen} className="rpt-section">
                  <summary>
                    <span className="rpt-nav-label">{section.title}</span>
                    <span style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                      <span className="rpt-section-count">
                        {chosen}/{keys.length}
                      </span>
                      <button
                        type="button"
                        className="rpt-btn--sm"
                        onClick={(e) => {
                          e.preventDefault();
                          setKeys(keys, chosen !== keys.length);
                        }}
                      >
                        {chosen === keys.length ? "Clear" : "Select all"}
                      </button>
                    </span>
                  </summary>
                  {section.fields.map((f) => renderRow(f, false))}
                </details>
              );
            })}

            {!sections.length && query && <div className="rpt-empty">No fields match &ldquo;{query}&rdquo;.</div>}
          </div>
        </div>

        <div className="add-contact-modal-footer rpt-footer-split">
          <p className="rpt-selected-count">
            <strong>{selected.length}</strong> field{selected.length === 1 ? "" : "s"} selected
            {selected.length > 0 && (
              <button type="button" className="rpt-link-btn" onClick={() => onChange([])}>
                Clear all
              </button>
            )}
          </p>
          <button type="button" className="add-contact-modal-submit" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
